import React, { useEffect, useState } from "react";
import { api_Routes } from "../../tools/api_Routes";
import { Helper } from "../../tools/Helper";
import {
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  TablePagination,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DataTable from "./data-table";
import SwitchButton from "./switch-button";
import {AddButton} from "../Button/AddButton";
import {ability} from "../../config/acl/ability";

export const AddBalanceRequestsTable = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [status, setStatus] = useState();
  const [paymentMethod, setPaymentMethod] = useState();
  const [panelUrl, setPanelUrl] = useState('');
  const [errorMessage, setErrorMessage] = useState("");
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [orderOption, setOrderOption] = useState("all");

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const getData = async () => {
    setErrorMessage("");

    // Validate that if a start date is selected, an end date must also be selected
    if (dateRange.start && !dateRange.end) {
      setErrorMessage("End date is required if a start date is selected.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    // Build the query string dynamically
    const queryParams = [];
    if (dateRange.start && dateRange.end) {
      queryParams.push(
        `start_date=${dateRange.start.toISOString().split("T")[0]}`
      );
      queryParams.push(`end_date=${dateRange.end.toISOString().split("T")[0]}`);
    } else if (dateRange.end)
      queryParams.push(`end_date=${dateRange.end.toISOString().split("T")[0]}`);
    if (status && status !== "null")
      queryParams.push(`order_status=${status}`);
    if (paymentMethod && paymentMethod !== "null")
      queryParams.push(`payment_method=${paymentMethod}`);
    if (panelUrl) queryParams.push(`panel_url=${encodeURIComponent(panelUrl)}`);
    const queryString = queryParams.length ? `${queryParams.join("&")}` : "";

    const url =`${
            api_Routes.AddBalanceRequests.View
          }?${queryString}&per_page=${rowsPerPage}&page=${currentPage + 1}`
        ;

    const { response, message } = await Helper.Get({
      url,
      hasToken: true,
    });
    if (response) {
      setData(response);
    } else {
      setData([]);
    }
    setIsLoading(false);
  };

  // Effect for filter changes
  useEffect(() => {
    if (filtersChanged) {
      setCurrentPage(0);
      setFiltersChanged(false);
    } else {
      getData();
    }
  }, [filtersChanged, currentPage, rowsPerPage, orderOption]);

  const handleResetFilters = () => {
    setCurrentPage(0);
    setDateRange({ start: null, end: null });
    if (status) setStatus("null");
    if (paymentMethod) setPaymentMethod("null");
    setPanelUrl('');
    setFiltersChanged(true);
  };

  const canAddBalanceRequest = ability.can('addBalanceRequests', 'store');

  return (
    <div>


      {/* Filter Section */}
      <div className="flex flex-col justify-between items-start mb-3 gap-4">
        {/* Date Range Filter */}
        <div className="w-full flex items-start flex-wrap lg:flex-nowrap justify-between gap-4">
          <div className="w-full">
            <LocalizationProvider dateAdapter={AdapterDayjs} className="w-full">
              <DatePicker
                label="Start Date"
                value={dateRange.start}
                className="w-full"
                onChange={(newValue) => {
                  setDateRange((prev) => ({ ...prev, start: newValue }));
                  setFiltersChanged(true);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </div>
          <div className="w-full">
            <LocalizationProvider dateAdapter={AdapterDayjs} className="w-full">
              <DatePicker
                label="End Date"
                value={dateRange.end}
                className="w-full"
                onChange={(newValue) => {
                  setDateRange((prev) => ({ ...prev, end: newValue }));
                  setFiltersChanged(true);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </div>
          <TextField
              label="Panel URL"
              value={panelUrl}
              onChange={(e) => {
                setPanelUrl(e.target.value);
                setFiltersChanged(true);
              }}
              className="w-full"
              fullWidth
          />

          {/* Payment Status Filter */}
          <FormControl className="w-full">
            <InputLabel id="payment_method-select-label">
              Payment Methods
            </InputLabel>
            <Select
              labelId="payment_method-select-label"
              id="payment-status-select"
              value={paymentMethod || null}
              onChange={(e) => {
                setPaymentMethod(e.target.value);
                setFiltersChanged(true);
              }}
              label="Payment Method"
              sx={{ textAlign: "start" }}
            >
              <MenuItem value="null">All</MenuItem>
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="bonus">Bonus</MenuItem>
              <MenuItem value="cryptomus">Cryptomus</MenuItem>
              <MenuItem value="payeer">Payeer</MenuItem>
              <MenuItem value="other">Other</MenuItem>
              <MenuItem value="none">None</MenuItem>
            </Select>
          </FormControl>

          {/* Order Status Filter */}
          <FormControl className="w-full">
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={status || null}
              onChange={(e) => {
                setStatus(e.target.value);
                setFiltersChanged(true);
              }}
              label="Status"
              sx={{ textAlign: "start" }}
            >
              <MenuItem value="null">All</MenuItem>
              <MenuItem value="DRAFT">Pending</MenuItem>
              <MenuItem value="ACCEPTED">ACCEPTED</MenuItem>
              <MenuItem value="REVOKED">REVOKED</MenuItem>
            </Select>
          </FormControl>
        </div>
        {errorMessage && <div>*{errorMessage}</div>}

        <div className={'flex justify-between'} style={{width:'100%'}}>
          {/* Reset Button */}
          <Button
              variant="outlined"
              color="primary"
              onClick={handleResetFilters}
              disabled={
                !dateRange.start &&
                !dateRange.end &&
                !panelUrl === '' &&
                (!status || status === "null") &&
                (!paymentMethod || paymentMethod === "null")
              }
          >
            Reset Filters
          </Button>
          {
            canAddBalanceRequest &&
            <AddButton to="/AddBalanceRequests/Add" label="Add Balance Request" />
          }

        </div>

      </div>

      {/* Table and Pagination */}
      {isLoading ? (
        <LinearProgress sx={{ marginTop: 1 }} />
      ) : data && data?.data?.length > 0 ? (
        <>
          <div className="overflow-x-auto">
              <DataTable
                setData={setData}
                setOrderOption={setOrderOption}
                data={data?.data}
                rowsPerPage={rowsPerPage}
                currentPage={currentPage}
                filters={{ dateRange, status, paymentMethod, panelUrl }} // Add this prop
              />

          </div>
          <div className="mt-4 flex justify-center items-center">
            <TablePagination
              page={currentPage}
              className="mt-auto"
              onPageChange={handlePageChange}
              count={data.pagination.total}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              variant="outlined"
              shape="rounded"
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </div>
        </>
      ) : (
        <TableContainer>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    fontWeight: "bold",
                  }}
                >
                  There are no data yet
                </TableCell>
              </TableRow>
            </TableHead>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};
